import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardDataTableSortButton,
  TablePagePagination,
  DefinitionTooltip,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { type MetaAdsPaginationMeta } from "src/services/MetaAdsDashboardAPI";
import {
  EMPTY_API_VALUE,
  formatOptionalNumber,
  getMetaColumnTooltip,
  getRoasToneClass,
  type AdSetSortKey,
  type LiveAdSetRow,
} from "./metaTableConfig";
import { formatPaidMediaCount, formatPaidMediaDateText, formatPaidMediaPercent, formatPaidMediaRoas } from "../../utils/paidMediaNumberFormat";

interface MetaAdSetsTableProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  adSetPaginationMeta: MetaAdsPaginationMeta;
  searchAdSets: string;
  setSearchAdSets: Dispatch<SetStateAction<string>>;
  setCurrentAdSetPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportAdSetsCsv: () => Promise<void>;
  toggleAdSetSort: (_key: AdSetSortKey) => void;
  paginatedAdSets: LiveAdSetRow[];
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  currentAdSetPage: number;
  adSetTotalPages: number;
}

const MetaAdSetsTable: FC<MetaAdSetsTableProps> = ({
  metaSectionHelp,
  adSetPaginationMeta,
  searchAdSets,
  setSearchAdSets,
  setCurrentAdSetPage,
  isExporting,
  exportAdSetsCsv,
  toggleAdSetSort,
  paginatedAdSets,
  formatMoney,
  currentAdSetPage,
  adSetTotalPages,
}) => {
  const { t } = useTranslation();

  return (
                <DashboardDataTablePanel className="paid-media-table-panel--attached paid-media-adsets-table-panel">
                  <DashboardDataTablePanelHeader>
                    <div className="paid-media-table-panel__heading-block">
                      <div className="paid-media-panel__heading">
                        <h3>{t("metaAdsReport.overviewSubTabs.adsets")}</h3>
                        <DefinitionTooltip
                          label={t("metaAdsReport.overviewSubTabs.adsets")}
                          info={metaSectionHelp("adSets")}
                        />
                      </div>
                      <p className="paid-media-table-panel__subtitle">
                        {t("metaAdsReport.sections.adSetsSubtitle", {
                          count: adSetPaginationMeta.total_items,
                        })}
                      </p>
                    </div>
                    <div
                      className="paid-media-table-toolbar"
                      role="toolbar"
                      aria-label={t("metaAdsReport.toolbars.adSetsSearch")}
                    >
                      <label className={`${dt.search} paid-media-table-toolbar__search`}>
                        <Search size={16} />
                        <input
                          type="search"
                          value={searchAdSets}
                          onChange={event => {
                            setSearchAdSets(event.target.value);
                            setCurrentAdSetPage(1);
                          }}
                          placeholder={t("metaAdsReport.placeholders.searchAdSets")}
                        />
                      </label>
                      <button
                        type="button"
                        className="paid-media-export-btn"
                        disabled={isExporting}
                        onClick={() => void exportAdSetsCsv()}
                      >
                        <Download size={16} aria-hidden />
                        {isExporting
                          ? t("metaAdsReport.export.exporting")
                          : t("metaAdsReport.export.csv")}
                      </button>
                    </div>
                  </DashboardDataTablePanelHeader>
                  <DashboardDataTableScroll>
                    <DashboardDataTableTable className="paid-media-adsets-performance-table">
                      <thead>
                        <tr>
                          <th>
                            <DashboardDataTableSortButton
                              onClick={() => toggleAdSetSort("name")}
                            >
                              {t("metaAdsReport.adSetTable.adSet")}
                            </DashboardDataTableSortButton>
                          </th>
                          {(
                            [
                              "budgetCategory",
                              "adsetStartAt",
                              "adsetEndAt",
                              "adsetCreatedAt",
                              "dailyBudget",
                              "linkClicks",
                            ] as const
                          ).map(column => (
                            <th key={column} className={dt.number}>
                              <span className={dt.thHelpRow}>
                                {t(`metaAdsReport.adSetTable.${column}`)}
                                <DefinitionTooltip
                                  label={t(`metaAdsReport.adSetTable.${column}`)}
                                  info={getMetaColumnTooltip(t, column, "adSet")}
                                  formulaTitle={t("webAnalytics.kpi.formula")}
                                />
                              </span>
                            </th>
                          ))}
                          {(
                            [
                              ["spend", dt.number],
                              ["impressions", ""],
                              ["conversions", ""],
                              ["ctr", ""],
                              ["roas", ""],
                            ] as const
                          ).map(([column, alignClass], columnIndex, columns) => (
                            <th key={column} className={alignClass || undefined}>
                              <span className={dt.thHelpRow}>
                                <DashboardDataTableSortButton
                                  onClick={() => toggleAdSetSort(column)}
                                >
                                  {t(`metaAdsReport.adSetTable.${column}`)}
                                </DashboardDataTableSortButton>
                                <DefinitionTooltip
                                  label={t(`metaAdsReport.adSetTable.${column}`)}
                                  info={getMetaColumnTooltip(t, column, "adSet")}
                                  align={
                                    columnIndex >= columns.length - 3 ? "end" : "start"
                                  }
                                  formulaTitle={t("webAnalytics.kpi.formula")}
                                />
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAdSets.map(row => (
                          <tr key={row.id}>
                            <td>
                              <span className="paid-media-table__entity-link">{row.name}</span>
                            </td>
                            <td className={dt.number}>{row.budgetCategory ?? EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{formatPaidMediaDateText(row.adsetStartAt) || EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{formatPaidMediaDateText(row.adsetEndAt) || EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{formatPaidMediaDateText(row.adsetCreatedAt) || EMPTY_API_VALUE}</td>
                            <td className={dt.number}>{formatOptionalNumber(row.dailyBudget, formatMoney)}</td>
                            <td className={dt.number}>{formatOptionalNumber(row.linkClicks)}</td>
                            <td className={dt.number}>{formatMoney(row.spend)}</td>
                            <td>{formatPaidMediaCount(row.impressions)}</td>
                            <td>{formatPaidMediaCount(row.conversions)}</td>
                            <td>{formatOptionalNumber(row.ctr, formatPaidMediaPercent)}</td>
                            <td className={getRoasToneClass(row.roas)}>{formatPaidMediaRoas(row.roas)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DashboardDataTableTable>
                  </DashboardDataTableScroll>
                  <TablePagePagination
                    currentPage={currentAdSetPage}
                    totalPages={adSetTotalPages}
                    onPrevious={() =>
                      setCurrentAdSetPage(page => Math.max(1, page - 1))
                    }
                    onNext={() =>
                      setCurrentAdSetPage(page =>
                        Math.min(adSetTotalPages, page + 1)
                      )
                    }
                />
              </DashboardDataTablePanel>
  );
};

export default MetaAdSetsTable;
